import { ApiResponse, IApiService } from "@/services/Api";
import { authActions, store, userActions } from "@/services/Store";
import { removeUrlTrailingSlash } from "@/utils/string";

import {
  IpInfo,
  OnboardStripeApiParams,
  PaymentCountryResponse,
  SubscribePremiumApiParams,
  Timezone,
  UpdateUserProfileParams,
  UserDetails,
  UserExistsResponse,
  UserInfoResponse,
  UserProfile,
  UserProfileUpdateFields,
  UserRegisterResponse,
} from "./UserService.types";

class UserService {
  _apiService: IApiService;

  constructor(_apiService: IApiService) {
    this._apiService = _apiService;
  }

  //------------------------

  registerUser = (data: Partial<Omit<UserDetails, "id">>) => {
    return this._apiService
      .post<UserRegisterResponse>("/auth/register", data)
      .then((res) => {
        store.dispatch(authActions.setUserData(res.data.data));

        return res;
      });
  };

  fetchUserInfo = () => {
    return this._apiService.get<UserInfoResponse>("/user").then((res) => {
      const userData = res.data?.data?.user;

      if (userData) store.dispatch(authActions.setUserData(userData));

      return res;
    });
  };

  updateUserInfo = (
    userInfo: UserProfileUpdateFields,
    config: UpdateUserProfileParams = {}
  ) => {
    const { notify = true } = config;

    return this._apiService
      .put<UserInfoResponse>(`/user?notify=${notify}`, userInfo)
      .then((res) => {
        const userData = res.data?.data?.user;

        if (userData) store.dispatch(authActions.setUserData(userData));

        return res;
      });
  };

  saveUserInfoToLocalStorage = (userInfo: Partial<UserProfile>) => {
    try {
      localStorage.setItem("PAYPIPE:USER_PROFILE", JSON.stringify(userInfo));
    } catch {}

    return Promise.resolve();
  };

  getUserInfoFromLocalStorage = () => {
    try {
      const userInfoString = localStorage.getItem("PAYPIPE:USER_PROFILE");
      const userInfo = (
        userInfoString ? JSON.parse(userInfoString) : {}
      ) as UserProfile;
      return userInfo;
    } catch {}

    return {} as UserProfile;
  };

  //------------------------

  onboardStripe = (params: OnboardStripeApiParams) => {
    const { countryCode } = params;
    let { refreshUrl, returnUrl } = params;

    if (!refreshUrl) {
      refreshUrl = window.location.href;
      refreshUrl = removeUrlTrailingSlash(refreshUrl);
    }

    if (!returnUrl) {
      returnUrl = window.location.href;
      returnUrl = removeUrlTrailingSlash(returnUrl);
    }

    const payload = {
      return_url: returnUrl,
      refresh_url: refreshUrl,
      country: countryCode,
    };

    return this._apiService.post<{
      url: string;
    }>("/payment/accountLink", payload);
  };

  isStripeOnboarded = () => {
    store.dispatch(
      userActions.setOnboardedState({
        isLoading: true,
      })
    );

    const isOnboarded = store.getState().user.isStripeOnboarded === true;
    if (isOnboarded) {
      return Promise.resolve(true);
    }

    return this._apiService
      .post<ApiResponse<{ onboarded: boolean; message: string }>>(
        "/payment/check-onboard-status"
      )
      .then((res) => {
        const isOnboarded = res.data.data.onboarded;

        store.dispatch(
          userActions.setOnboardedState({
            isOnboarded,
          })
        );

        return isOnboarded;
      })
      .finally(() => {
        store.dispatch(
          userActions.setOnboardedState({
            isLoading: false,
          })
        );
      });
  };

  getPaymentSupportedCountries() {
    return this._apiService.post<ApiResponse<PaymentCountryResponse>>(
      "/payment/supported-countries"
    );
  }

  checkWhetherUserExists(data: { email: string }) {
    const { email } = data;
    return this._apiService.anonGet<ApiResponse<UserExistsResponse>>(
      `/auth/user_exists?email=${email}`
    );
  }

  fetchTimezoneList() {
    return this._apiService
      .anonGet<Timezone[]>(`/assets/json/timezones.json`, { baseURL: "/" })
      .then((res) => res.data);
  }

  updateUserProfileImage(file: File) {
    const formdata = new FormData();
    formdata.append("image", file, file.name);

    return this._apiService
      .post<UserRegisterResponse>("/profile-upload", formdata)
      .then((res) => {
        store.dispatch(authActions.setUserData(res.data.data));

        return res;
      });
  }

  getClientCountry() {
    return this._apiService.anonGet<IpInfo>("", {
      baseURL: "https://ipinfo.io",
    });
  }

  //------------------------

  subscribeToPremium(params: SubscribePremiumApiParams) {
    const { interval } = params;
    let { successUrl, cancelUrl } = params;

    if (!successUrl) {
      successUrl = window.location.href;
      successUrl = removeUrlTrailingSlash(successUrl);
    }

    if (!cancelUrl) {
      cancelUrl = window.location.href;
      cancelUrl = removeUrlTrailingSlash(cancelUrl);
    }

    const payload = {
      successUrl,
      cancelUrl,
      interval,
    };

    return this._apiService
      .post<
        ApiResponse<{
          url: string;
        }>
      >("payment/subscribe", payload)
      .then((res) => res.data);
  }

  //------------------------

  cleanup = () => {
    store.dispatch(
      userActions.setOnboardedState({
        isOnboarded: false,
      })
    );
  };
}

export default UserService;
